//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import {
  CuralateFacebookFeature,
  CuralateFacebookFeatureInfo,
  CuralateInstagramFeature,
  CuralateInstagramFeatureInfo,
  ICuralateFacebookFeatureRequirements,
} from '@account-settings/redux/facebook-instagram/curalateFbIgFeatureInfoModel';
import { getHasMultiHandleInstagramEnabled } from '@account-settings/redux/facebook-instagram/selectors/getMultiHandleFeature';
import { ICuralateStore } from '@models/AppState';
import FacebookPageTask from '@models/FacebookPageTask';
import FacebookUserPermission from '@models/FacebookUserPermission';

import { SessionFeatureMap } from 'app/basic-session/ICuralateSession';

const checkIfHasAllRequired = <T>(all: Set<T>, required: T[]) =>
  required.every((e) => all.has(e));

const checkForCuralateFbIgFeature = (
  permissions: Set<FacebookUserPermission>,
  tasks: Set<FacebookPageTask>,
  features: SessionFeatureMap,
  featureRequirements: ICuralateFacebookFeatureRequirements[],
): boolean => {
  const hasAtLeastOneFeature = featureRequirements.some((featureRequirement) =>
    featureRequirement.feature ? !!features[featureRequirement.feature] : true,
  );
  const hasEveryPermissionForActiveFeatures = featureRequirements.every(
    (featureRequirement) => {
      const hasReqPermissions = checkIfHasAllRequired(
        permissions,
        featureRequirement.permissions,
      );
      const hasReqTasks = checkIfHasAllRequired(
        tasks,
        featureRequirement.tasks,
      );
      const hasReqFeature = featureRequirement.feature
        ? !!features[featureRequirement.feature]
        : true;
      return !hasReqFeature || (hasReqPermissions && hasReqTasks);
    },
  );
  return hasAtLeastOneFeature && hasEveryPermissionForActiveFeatures;
};

export const getUserTokenPermissions = createSelector(
  (state: ICuralateStore) =>
    state.facebookInstagramSettings.facebookUserToken?.permissions,
  (permissions) => new Set<FacebookUserPermission>(permissions ?? []),
);

export const getInstagramAccountTaskSet = createSelector(
  (state: ICuralateStore) => {
    const isMutliHandleFeatureEnabled =
      getHasMultiHandleInstagramEnabled(state);

    if (!isMutliHandleFeatureEnabled) {
      // Single user token permissions
      return (
        state.facebookInstagramSettings.connectedInstagramAccount?.tasks ?? []
      );
    } else {
      // Filter default accounts and get their tasks
      return (
        state.facebookInstagramSettings.connectedInstagramAccounts
          ?.filter((account) => account.isDefault)
          .flatMap((account) => account.tasks) ?? []
      );
    }
  },
  (tasks) => new Set<FacebookPageTask>(tasks),
);

const getFacebookPageTaskSet = createSelector(
  (state: ICuralateStore) =>
    state.facebookInstagramSettings.connectedFacebookPageToken?.tasks,
  (tasks) => new Set<FacebookPageTask>(tasks ?? []),
);

export const getSessionFeatures = (state: ICuralateStore) =>
  state.session.features;

/**
 * Get a list of all potentially available Instagram Features,
 * defined by their enabled client features. (Doesn not check permissions or tokens)
 */
export const getAvailableFacebookFeatures = createSelector(
  getSessionFeatures,
  (clientFeatures) =>
    PojoSet.from(
      Object.values(CuralateFacebookFeature).filter((feature) =>
        CuralateFacebookFeatureInfo[feature].requirements.some(
          (requirement) => {
            const requiredClientFeature = requirement.feature;
            return (
              !requiredClientFeature ||
              PojoSet.has(clientFeatures, requiredClientFeature)
            );
          },
        ),
      ),
    ),
);

export const getFacebookFeatures = (
  permissions: Set<FacebookUserPermission>,
  tasks: Set<FacebookPageTask>,
  features: SessionFeatureMap,
) =>
  PojoSet.from(
    Object.values(CuralateFacebookFeature).filter((feature) => {
      const featureRequirements =
        CuralateFacebookFeatureInfo[feature].requirements;
      return checkForCuralateFbIgFeature(
        permissions,
        tasks,
        features,
        featureRequirements,
      );
    }),
  );

/**
 * This selector computes a PojoSet of the Curalate Facebook features that the client has based on
 * their current FB/IG setup as represented by their connected FB user token, page token, and their
 * Curalate client level features.
 */
export const getClientFacebookFeatures = createSelector(
  getUserTokenPermissions,
  getFacebookPageTaskSet,
  getSessionFeatures,
  getFacebookFeatures,
);

/**
 * Get a list of all potentially available Instagram Features,
 * defined by their enabled client features. (Doesn not check permissions or tokens)
 */
export const getAvailableInstagramFeatures = createSelector(
  getSessionFeatures,
  (clientFeatures) =>
    PojoSet.from(
      Object.values(CuralateInstagramFeature).filter((feature) =>
        CuralateInstagramFeatureInfo[feature].requirements.some(
          (requirement) => {
            const requiredClientFeature = requirement.feature;
            return (
              !requiredClientFeature ||
              PojoSet.has(clientFeatures, requiredClientFeature)
            );
          },
        ),
      ),
    ),
);

export const getInstagramFeatures = (
  permissions: Set<FacebookUserPermission>,
  tasks: Set<FacebookPageTask>,
  features: SessionFeatureMap,
) =>
  PojoSet.from(
    Object.values(CuralateInstagramFeature).filter((feature) => {
      const featureRequirements =
        CuralateInstagramFeatureInfo[feature].requirements;
      return checkForCuralateFbIgFeature(
        permissions,
        tasks,
        features,
        featureRequirements,
      );
    }),
  );

/**
 * This selector computes a PojoSet of the Curalate Instagram features that the client has based on
 * their current FB/IG setup as represented by their connected FB user token, the tasks associated
 * with the Facebook page linked to their connected Instagram business account, and their Curalate
 * client level features.
 */
export const getClientInstagramFeatures = createSelector(
  getUserTokenPermissions,
  getInstagramAccountTaskSet,
  getSessionFeatures,
  getInstagramFeatures,
);

export const getHasInstagramManageMessagesFeatureAndPermissions =
  createSelector(
    getClientInstagramFeatures,
    (clientInstagramFeatures) =>
      !!clientInstagramFeatures.InstagramManageMessages,
  );
